import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box, Text } from '@primer/react';
import Code from '@primer/gatsby-theme-doctocat/src/components/code';
import ComponentLayout from '~/src/layouts/component-layout';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <h2>{`Usage`}</h2>
    <p>{`Links enable users to navigate between pages or resources in a seamless way. Links can be used within a body of text or as a standalone element.`}</p>
    <p>{`Links navigate you to a new place or new content. `}<strong parentName="p">{`Do not`}</strong>{` use links for triggering an action. Instead, use `}<a parentName="p" {...{
        "href": "/components/button"
      }}>{`buttons`}</a>{`, which are designed to activate a feature.`}</p>
    <p>{`Links should be concise and not exceed more than a few words.`}</p>
    <h2>{`Anatomy`}</h2>
    <p>{`The link component can be displayed in default, muted, or blue colors. Links can be underlined, may include a leading visual or trailing visual, and can be either the default or small size. `}</p>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="400" role="presentation" src="https://user-images.githubusercontent.com/586552/225489084-4f4357b4-4c47-4a55-8770-76e03b8c500f.png" />
  <img width="369" role="presentation" src="https://user-images.githubusercontent.com/586552/225489086-e604fbc8-e449-416e-bd26-cc80408c5d8d.png" />
    </Box>
    <h2>{`Accessibility`}</h2>
    <p>{`Links play a key part in your experience on the web, but without proper consideration they can be frustrating to use, skipped over, or completely unnoticed.`}</p>
    <p>{`For assistive technologies like screen readers, links and buttons are expected to function differently from each other. If a link is activated and does not do what was expected, that can be disorienting and frustrating.`}</p>
    <p>{`A common way a screen reader might navigate the page is by going through a list of all the links on the page. Without context, "read more" or "click here" links are not helpful.`}</p>
    <p>{`People who have low or colorblind vision may have trouble identifying links that just use color to distinguish them from plain text, this is why keeping the underline styling on links within body text is important for identification.`}</p>
    <h3>{`How to test links`}</h3>
    <h4>{`Functionality and purpose`}</h4>
    <p>{`A link's function is to `}<em parentName="p">{`navigate`}</em>{` to a different page or new content. If instead a feature on the page is activated, use a `}<inlineCode parentName="p">{`<button>`}</inlineCode>{`. `}<a parentName="p" {...{
        "href": "https://accessibility-playbook.github.com/guidance/patterns/link-and-button-guidance"
      }}>{`Learn when to use a link or button`}</a>{`.`}</p>
    <p>{`A link's purpose must be obvious from the link text alone. If you can't get an idea of where a link will take you based on the link text without reading the sourrounding text, the link text should be updated. `}<a parentName="p" {...{
        "href": "#writing-link-text"
      }}>{`Learn how to write good link text`}</a>{`.`}</p>
    <p>{`This is important because screen readers allow users to browse through a list of links, where the link text is the only clue of where a link will take you.`}</p>
    <h4>{`Visual distinction & contrast`}</h4>
    <p>{`Like normal text, a link must have a `}<inlineCode parentName="p">{`4.5:1`}</inlineCode>{` contrast against the background color that it is placed on. Use a `}<a parentName="p" {...{
        "href": "https://webaim.org/resources/contrastchecker/"
      }}>{`contrast checker`}</a>{` to validate that your link meets this required contrast.`}</p>
    <p>{`If a link is sourrounded by text, it must be underlined or pass a `}<inlineCode parentName="p">{`3:1`}</inlineCode>{` contrast against the sourrounding text as well. Alternativly an icon, a background shape or an outline can demarcate a link.`}</p>
    <p>{`Some examples of this are:`}</p>
    <ul>
      <li parentName="ul">{`links within body text`}</li>
      <li parentName="ul">{`a headline and subline which both are individual links`}</li>
      <li parentName="ul">{`issue numbers or usernames within a commit line`}</li>
    </ul>
    <h3>{`Guidelines`}</h3>
    <ul>
      <li parentName="ul">{`Visually demarcate your links by using:`}
        <ul parentName="li">
          <li parentName="ul">{`the `}<inlineCode parentName="li">{`accent.fg`}</inlineCode>{` color in combination with `}<inlineCode parentName="li">{`fg.default`}</inlineCode>{` for the sourrounding text on any of the `}<inlineCode parentName="li">{`canvas.[...]`}</inlineCode>{` colors`}</li>
          <li parentName="ul">{`an underline for the link text`}</li>
          <li parentName="ul">{`an icon before or after the link text`}</li>
          <li parentName="ul">{`using a background shape behind the link`}</li>
          <li parentName="ul">{`using a link color that has a `}<inlineCode parentName="li">{`3:1`}</inlineCode>{` contrast against the sourrounding text color AND a `}<inlineCode parentName="li">{`4.5:1`}</inlineCode>{` against the background color`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Make sure a link's purpose can be understood from the link text alone, without needing the surrounding context.`}</li>
      <li parentName="ul">{`Links should look like links, not buttons, except in rare circumstances, like calls to action.`}</li>
    </ul>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/40274682/160483376-a88eb64f-ce80-4a11-93b5-6d882b1970eb.png" alt="Markdown paragraph with descriptive links" />
    <Caption mdxType="Caption">Be descriptive with your links so that they can stand alone and be understood.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/40274682/160474793-2fd3085f-0813-43ed-8b43-c1a5f14aaea9.png" alt="Markdown paragraph with links like 'Click here' and 'find out more'" />
    <Caption mdxType="Caption">Don't use generic terms like 'click here' that can't be understood out of context.</Caption>
  </Dont>
    </DoDontContainer>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/40274682/160474794-06a75b30-06d3-475e-863f-605e8a6dad72.png" alt="Underlined username link in issue subheading" />
    <Box sx={{
          marginTop: 2,
          backgroundColor: 'neutral.subtle',
          borderRadius: 6
        }} mdxType="Box">
      <img width="550" alt="Underlined links in paragraph" src="https://user-images.githubusercontent.com/586552/225663474-e54cf0aa-5b1a-4f24-bd31-7502127e6046.png" />
    </Box>
    <Caption mdxType="Caption">Underline links in paragraphs and sentences</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/40274682/160474800-b505369e-1730-4c14-b426-70b5bd016a6c.png" alt="Issue subheader with none of the links underlined" />
    <Box sx={{
          backgroundColor: 'neutral.subtle',
          marginTop: 2,
          borderRadius: 6
        }} mdxType="Box">
      <img width="550" alt="Links using underline to highlight important text instead of link" src="https://user-images.githubusercontent.com/586552/225663469-b8899b88-59f4-48a6-8e48-9ec3d0d45b37.png" />
    </Box>
    <Caption mdxType="Caption">Don't use underlines for non-link highlights or forget to demarcate links</Caption>
  </Dont>
    </DoDontContainer>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <Box sx={{
          backgroundColor: 'neutral.subtle',
          borderRadius: 6
        }} mdxType="Box">
      <img width="550" alt="Links using the accent color and a muted grey both with a 3:1 contrast against the default text color" src="https://user-images.githubusercontent.com/586552/225663478-c519d894-b898-42dd-b426-c6e260ac1341.png" />
    </Box>
    <Caption mdxType="Caption">Use a link color that has a 3:1 contrast against the text color</Caption>
  </Do>
  <Dont mdxType="Dont">
    <Box sx={{
          backgroundColor: 'neutral.subtle',
          borderRadius: 6
        }} mdxType="Box">
      <img width="550" alt="Links using a color with a contrast smaller than 3:1 against the default text color" src="https://user-images.githubusercontent.com/586552/225663473-44503373-39f8-4eee-bb9e-ed82442bb47b.png" />
    </Box>
    <Caption mdxType="Caption">Don't use a color that has less than a 3:1 contrast against the text color for links</Caption>
  </Dont>
    </DoDontContainer>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <Box sx={{
          backgroundColor: 'neutral.subtle',
          borderRadius: 6
        }} mdxType="Box">
      <img width="550" alt="Links with icon or background shape to demarcate it" src="https://user-images.githubusercontent.com/586552/225663476-44cc0c51-705c-4b32-98ea-4a7db9ba9876.png" />
    </Box>
    <Caption mdxType="Caption">Use an icon or a background shape to demarcate a link</Caption>
  </Do>
  <Dont mdxType="Dont">
    <Box sx={{
          backgroundColor: 'neutral.subtle',
          borderRadius: 6
        }} mdxType="Box">
      <img width="550" alt="Links using other text styles to demaracte it" src="https://user-images.githubusercontent.com/586552/225663470-4c66fea4-2e5d-4999-bfe1-44733ba1fa2a.png" />
    </Box>
    <Caption mdxType="Caption">Don't use text styles like italic or bold to demarcate links</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`For engineers`}</h3>
    <ul>
      <li parentName="ul">{`Use Primer link components:`}
        <ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://primer.style/view-components/components/link"
            }}>{`Link in Primer ViewComponents`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://primer.style/react/Link"
            }}>{`Link in Primer React`}</a></li>
        </ul>
      </li>
      <li parentName="ul">{`Don't override the link styling provided in the components`}</li>
      <li parentName="ul">{`Make sure links receive our global focus indicator styling when navigating the page with a keyboard (reference: `}<a parentName="li" {...{
          "href": "/guides/accessibility/focus-management"
        }}>{`Focus management`}</a>{`)`}</li>
      <li parentName="ul">{`Don't use the `}<inlineCode parentName="li">{`title`}</inlineCode>{` attribute`}
        <ul parentName="li">
          <li parentName="ul">{`Content within a `}<inlineCode parentName="li">{`title`}</inlineCode>{` is inaccessible for many users, such as touch-only and keyboard-only users. If additional content needs to be associated with a link consider using a `}<a parentName="li" {...{
              "href": "https://primer.style/view-components/components/alpha/tooltip"
            }}>{`tooltip`}</a>{` or `}<a parentName="li" {...{
              "href": "/guides/accessibility/tooltip-alternatives"
            }}>{`alternatives to a tooltip`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Avoid adding side effects to link click events. Links should navigate, not affect the page.`}</li>
      <li parentName="ul">{`Don't force links to open in a new tab/window by setting the `}<inlineCode parentName="li">{`target`}</inlineCode>{` property.`}</li>
      <li parentName="ul">{`Links should always open in a new window when clicking while holding Command/Control.`}</li>
    </ul>
    <h3>{`Common mistakes`}</h3>
    <ul>
      <li parentName="ul">{`Only adding an underline to a link when it has focus or is hovered over`}</li>
      <li parentName="ul">{`Underlining every link on the page. For example, a navigation list is a list of links but navigational links don't have to be underlined because the intent is understood and an underline is not needed to identify the interactive nature of the control.`}</li>
    </ul>
    <h3>{`Writing link text`}</h3>
    <p>{`Link text should be descriptive enough to convey the destination without relying on the surrounding text. Screen reader users often tab through links on a page to quickly find content without needing to listen to the full page.
When link text is too generic like "Read more", the destination of the link is not conveyed.`}</p>
    <p>{`It may be acceptable in some scenarios to provide a more descriptive link text for screen reader users by setting the `}<inlineCode parentName="p">{`aria-label`}</inlineCode>{`. However, this technique will result in divergence between the label and the text and is not an ideal, future-proof solution.
Whenever possible, prefer a single descriptive link text that is available for both sighted users and screen reader users.`}</p>
    <p>{`If you must resort to the ARIA technique to provide a descriptive link text, follow these principles:`}</p>
    <ol>
      <li parentName="ol">{`The visible text is included in full as part of the accessible name to avoid a violation of `}<a parentName="li" {...{
          "href": "https://www.w3.org/WAI/WCAG21/Understanding/label-in-name.html"
        }}>{`SC 2.5.3: Label in Name`}</a>{`.`}</li>
      <li parentName="ol">{`The sentence that the link is a part of is well-formed and grammatical when read with both the visible text and the accessible name.`}</li>
    </ol>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <Caption mdxType="Caption">
      Accessible name fully includes the visible link text, "Learn more" and is well-formed with either label.
    </Caption>
    <Code className="language-html" mdxType="Code">{`There are various plans available. 
<a href="..." aria-label="Learn more about GitHub pricing plans">Learn more</a>`}</Code>
    <br />
  </Do>
  <Dont mdxType="Dont">
    <Caption mdxType="Caption">
      Accessible name results in poorly-formed sentence, "Learn more about keyboard shortcuts allow you to access common
      commands more quickly".{' '}
    </Caption>
    <Code className="language-html" mdxType="Code">{`<a href="..." aria-label="Learn more about keyboard shortcuts">Keyboard shortcuts</a> allow you to access common commands more quickly.`}</Code>
  </Dont>
    </DoDontContainer>
    <p>{`As demonstrated in the examples, this technique adds more complexity to the code and can introduce more problems than it solves so only use this technique if absolutely necessary.`}</p>
    <h3>{`Additional resources`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://dequeuniversity.com/class/visual-design/color/distinguish-links-from-text"
        }}>{`Color to Distinguish Links from Text`}</a>{` (requires a subscription to access the content)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://accessibilityinsights.io/en/"
        }}>{`Accessibility Insights plugin`}</a>{`: With the Accessibility Insights plugin installed, open the plugin, visit the `}<strong parentName="li">{`Assessment`}</strong>{` option and select `}<strong parentName="li">{`7. Links`}</strong></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://webaim.org/techniques/hypertext/"
        }}>{`WebAIM: Links and Hypertext - Introduction to Links and Hypertext`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/erblint-github/blob/main/docs/rules/accessibility/avoid-generic-link-text-counter.md"
        }}>{`erblint-github: Avoid generic link text`}</a></li>
    </ul>
    <h4>{`Related WCAG guidelines and success criteria`}</h4>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html"
        }}>{`Understanding Success Criterion 4.1.2: Name, Role, Value`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-practices-1.2/#link"
        }}>{`Authoring Practices Guide - Links`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-1.2/#link"
        }}>{`Accessible Rich Internet Applications (WAI-ARIA) 1.2 (w3.org)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/WCAG21/Understanding/link-purpose-in-context.html"
        }}>{`Understanding Success Criterion 2.4.4: Link Purpose (In Context)`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      